import React from 'react';
import { ArrowRight } from '../../components/Glyph';
import { Card, Kind, Greeting, FourBar, Need, NeedsCard, HomeFrame, today, dueState, DueChip } from './parts';
import ReleaseNote from '../../components/ReleaseNote';

/*
  Manager Home.

  Built 5 September 2026 against the approved drawing in docs/design/group-c/
  (signed off 31 August 2026). Same three blocks as the other two Homes and the
  same furniture, and it asks a different question, which is the whole reason the
  three Homes stayed three rather than becoming one:

    An admin asks   what needs approving
    A manager asks  who is behind
    A rep asks      what do I owe

    1  Your regions      how far the manager's own regions have got
    2  Who is behind     the three slowest reps, and the locations nobody is on
    3  Your own counts   a manager can hold locations too, and usually does not

  **Block three is the one to argue with.** A manager is a person who covers
  regions AND, sometimes, locations of their own. The drawing keeps the block
  even when it is empty rather than hiding it, because a block that appears and
  disappears is a block nobody learns the shape of, and "you have none" is a
  useful thing to be told once.
*/

export default function ManagerHome({
  profile, cycle, regionNames = [], stats, total, pct,
  behindRows = [], unassignedCount = 0, myCounts = [],
  onOpenCycle, onOpenReps, onOpenMyCounts, onOpenCount,
}) {
  const left = stats.not_started + stats.in_progress;
  const due  = dueState(cycle?.due_on);

  const line = cycle
    ? `${today()}. Your regions are ${pct}% counted and ${left} location${left === 1 ? '' : 's'} ${left === 1 ? 'has' : 'have'} not been finished.${due ? ' ' + due.line : ''}`
    : `${today()}. No count cycle is open.`;

  const headline = cycle ? (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Kind>Your regions in {cycle.name}</Kind>
        <DueChip due={due} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', margin: '8px 0 14px' }}>
        <div>
          <span style={{ fontSize: 34, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.6px' }}>{total}</span>
          <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
            location{total === 1 ? '' : 's'} across {regionNames.length} region{regionNames.length === 1 ? '' : 's'}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <Kind>Done</Kind>
          <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--action-text)', lineHeight: 1.05 }}>{pct}%</div>
        </div>
      </div>
      <FourBar notStarted={stats.not_started} inProgress={stats.in_progress}
        submitted={stats.submitted} approved={stats.approved} />
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 14, lineHeight: 1.55 }}>
        {regionNames.join(' - ') || 'No regions are assigned to you.'}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" style={{ height: 44 }} onClick={onOpenCycle}>Open Cycle Details</button>
      </div>
    </Card>
  ) : (
    <Card pad="32px 24px" style={{ textAlign: 'center' }}>
      <div className="oh-title" style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>No count cycle is open</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.6, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
        Nobody in your regions can count until an administrator opens one.
      </div>
    </Card>
  );

  // **Who is behind, not who is worst.** The number on each row is how many
  // locations that rep has NOT finished, because that is the thing a manager
  // can act on. The percentage is on the line under it, where it belongs: it
  // says how they are doing, and the count says how much is left.
  const behind = (
    <NeedsCard title="Who is behind" footer={<>See every rep <ArrowRight size={13} /></>} onFooter={() => onOpenReps()}
      rows={
        <>
          {behindRows.map(r => (
            <Need key={r.key} n={r.left} title={r.name}
              sub={`${r.total} location${r.total === 1 ? '' : 's'}, ${r.pct}% done`}
              tone="var(--critical-ink)" onClick={() => onOpenReps(r.key)} />
          ))}
          <Need n={unassignedCount} title="Nobody assigned"
            sub="Locations in your regions with no rep"
            tone="var(--critical-ink)" onClick={() => onOpenReps('__unassigned__')} />
        </>
      } />
  );

  const mine = (
    <Card>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)' }}>Your own counts</div>
      {myCounts.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.6 }}>
          You have no locations of your own in this cycle.
        </div>
      ) : (
        <div style={{ marginTop: 4 }}>
          {myCounts.slice(0, 4).map(c => (
            // `live`: a sheet not started yet is exactly the one to open, and
            // its 0 dimmed it and switched it off (issue 21, 18 September 2026).
            <Need key={c.id} n={c.done} title={c.name} sub={c.sub} live
              tone="var(--accent-ink)" onClick={() => onOpenCount(c.id)} />
          ))}
          {myCounts.length > 4 && (
            <button type="button" onClick={onOpenMyCounts}
              style={{ marginTop: 12, border: 'none', background: 'transparent', font: 'inherit', fontSize: 12.5, fontWeight: 700, color: 'var(--link)', cursor: 'pointer', padding: 0 }}>
              All {myCounts.length} of yours <ArrowRight size={13} />
            </button>
          )}
        </div>
      )}
    </Card>
  );

  return (
    <div>
      {/* **What changed in OnHand, above the greeting.** The owner's own
          instruction of 5 September 2026: the release note stops taking the
          whole screen and becomes a notice here with one button that clears
          it. It draws nothing at all when there is no unread note, which is
          almost always. Group H's drawing, and it changes this approved
          Group C screen because he asked for it. */}
      <ReleaseNote />
      <Greeting name={profile?.full_name} line={line} />
      <HomeFrame headline={headline} needs={behind} third={mine} />
    </div>
  );
}
